import React, { useState, useEffect, ReactElement, ComponentProps } from "react";
import { Button, Col, Dropdown, Row } from "react-bootstrap";
import { UIGridBreakpoints } from "src/common";
import { Icon, ToolbarButton, ToolbarDropdownItem, Pagination, SimplePagination } from "src/components";

type ToolbarButtonElement = ReactElement<ComponentProps<typeof ToolbarButton>, typeof ToolbarButton>;
type ToolbarDropdownItemElement = ReactElement<ComponentProps<typeof ToolbarDropdownItem>, typeof ToolbarDropdownItem>;
type PaginationElement = ReactElement<ComponentProps<typeof Pagination>, typeof Pagination>;
type SimplePaginationElement = ReactElement<ComponentProps<typeof SimplePagination>, typeof SimplePagination>;
type ToolbarWrapperChild = ToolbarButtonElement | ToolbarDropdownItemElement | ReactElement<ComponentProps<"span">, "span">;
type ToolbarPaginationChild = PaginationElement | SimplePaginationElement;

type AllProps = {
  children?: ToolbarWrapperChild | ToolbarWrapperChild[];
  leftContent?: ReactElement;
  pagination?: ToolbarPaginationChild;
  paginationInfo?: ReactElement;
  refreshTooltipContent?: string;
  refreshDisabled?: boolean;
  isLoading: boolean;
  disabled?: boolean;
  refreshAction: () => void;
}

// Description: Check if the viewport is smaller than the sm breakpoint (576px)
function isViewportSm(): boolean {
  const w = typeof window !== "undefined" ? window.innerWidth : 0;
  return w < UIGridBreakpoints.sm;
}

// Description: Recursively flattens React fragments so ToolbarWrapper can inspect children regardless of how they are grouped.
function flattenChildren(children: React.ReactNode): React.ReactNode[] {
  return React.Children.toArray(children).flatMap((child) => {
    if (React.isValidElement(child) && child.type === React.Fragment) {
      return flattenChildren((child.props as { children?: React.ReactNode }).children);
    }
    return [child];
  });
}

// Description: Determine if there is any non-hidden ToolbarDropdownItem in the children
function hasNonHiddenDropdownItems(children: React.ReactNode): boolean {
  const child_array = flattenChildren(children);
  return child_array.some((child) => React.isValidElement(child) && child.type === ToolbarDropdownItem && !(child.props as { hidden?: boolean }).hidden);
}

// Description: Determine if there is any non-disabled ToolbarDropdownItem in the children
function hasNonDisabledDropdownItems(children: React.ReactNode): boolean {
  const child_array = flattenChildren(children);
  return child_array.some((child) => React.isValidElement(child) && child.type === ToolbarDropdownItem && !(child.props as { disabled?: boolean }).disabled);
}

// Description: Toolbar wrapper component.
function ToolbarWrapperComponent(props: AllProps) {
  const { leftContent, children, pagination, paginationInfo, refreshAction, refreshTooltipContent, refreshDisabled, isLoading, disabled } = props;
  const [show_menu, setShowMenu] = useState(false);
  const [viewport_is_sm, setViewportIsSm] = useState(isViewportSm);

  const child_array = flattenChildren(children);
  const button_items = child_array.filter((child) => React.isValidElement(child) && (child.type === ToolbarButton));
  const inline_items = child_array.filter((child) => React.isValidElement(child) && (child.type === ToolbarButton || child.type === "span"));
  const dropdown_items = child_array.filter((child) => React.isValidElement(child) && child.type === ToolbarDropdownItem && !(child.props as { hidden?: boolean }).hidden);

  // Description: When in mobile view (viewport sm) and there are more than 3 ToolbarButtons, render ToolbarButton as ToolbarDropdownItem instead and hide it if it is disabled
  const button_items_as_dropdown_items = button_items.map((child, index) => {
    if (!React.isValidElement(child) || child.type !== ToolbarButton) return null;
    const p = child.props;
    return (
      <ToolbarDropdownItem key={child.key ?? index} onClick={p.onClick} disabled={p.disabled} hidden={p.disabled} tooltipContent={p.tooltipContent}>
        {p.ariaLabel}
      </ToolbarDropdownItem>
    );
  });

  // Description: When in mobile view (viewport sm) and there are ToolbarDropdownItems, hide the ToolbarDropdownItem if it is disabled
  const dropdown_items_small_view = dropdown_items.map((child, index) => {
    if (!React.isValidElement(child) || child.type !== ToolbarDropdownItem) return null;
    const p = child.props;
    return (
      <ToolbarDropdownItem key={child.key ?? index} onClick={p.onClick} disabled={p.disabled} hidden={p.disabled} tooltipContent={p.tooltipContent}>
        {p.children}
      </ToolbarDropdownItem>
    );
  });

  useEffect(() => {
    const onResize = () => setViewportIsSm(isViewportSm());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return <Row className={`toolbar${disabled ? " disabled" : ""}`}>
    {/* Left Content */}
    {!!leftContent && (
      <Col xs="auto" className="order-0 align-center pe-0">
        {leftContent}
      </Col>
    )}

    {/* Toolbar Buttons */}
    <Col className="order-1">
      {viewport_is_sm && button_items.length > 3 ? (
        (hasNonHiddenDropdownItems(button_items_as_dropdown_items) || hasNonHiddenDropdownItems(dropdown_items_small_view)) && (
          <span data-tooltip-id="tb-tooltip" data-tooltip-content="More actions">
            <Dropdown as="button" className="btn btn-icon btn-sm" onToggle={() => setShowMenu((prev) => !prev)} show={show_menu}>
              <Dropdown.Toggle aria-label="More actions..." as="span">
                <Icon className="ficon-more-vertical" />
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                {button_items_as_dropdown_items}
                {dropdown_items_small_view}
              </Dropdown.Menu>
            </Dropdown>
          </span>
        )
      ) : (
        <>
          {inline_items}
          {dropdown_items.length > 0 && hasNonDisabledDropdownItems(dropdown_items) && (
            <span data-tooltip-id="tb-tooltip" data-tooltip-content="More actions">
              <Dropdown as="button" className="btn btn-icon btn-sm" onToggle={() => setShowMenu((prev) => !prev)} show={show_menu}>
                <Dropdown.Toggle aria-label="More actions..." as="span">
                  <Icon className="ficon-more-vertical" />
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  {dropdown_items}
                </Dropdown.Menu>
              </Dropdown>
            </span>
          )}
        </>
      )}
    </Col>

    {/* Pagination */}
    <Col xs="auto" className="order-sm-2 order-3 ms-auto">
      {pagination}
    </Col>

    {/* Pagination Info */}
    <Col xs="auto" className={`order-sm-3 order-4 pagination_info${refreshDisabled ? " me-2" : ""}`}>
      {paginationInfo}
    </Col>

    {/* Refresh Button */}
    {!refreshDisabled && (
    <Col xs="auto" className="p-0 order-sm-3 order-4 ms-1 refresh">
      <span className="divider me-1" />
      <Button variant="icon" size="sm"
        onClick={refreshAction}
        aria-label={refreshTooltipContent ?? "Refresh"}
        data-tooltip-id="tb-tooltip"
        data-tooltip-content={refreshTooltipContent ?? "Refresh"}>
        {
          isLoading ?
            <i className="spinner"></i> :
            <Icon className="ficon-rotate-cw" />
          }
        </Button>
      </Col>
    )}
  </Row>;
}

export default React.memo(ToolbarWrapperComponent);
